import React, { useCallback } from 'react';
import { Box, Button, Heading, Loader, TextButton } from '@wix/design-system';
import { AiSparkleIcon } from './AiSparkleIcon';
import RegenerateSparkle from './RegenerateSparkle.svg';
import { useIsMobile } from '../../hooks/useIsMobile';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { BusinessNameRow } from './BusinessNameRow';
import { BusinessNameTiles } from './BusinessNameTiles';
import { BusinessNamesMobileCtaSkeleton, BusinessNameTileSkeletons } from './BusinessNameTileSkeleton';
import { BusinessNamesError } from './BusinessNamesError';
import { useBusinessNamesSection } from './useBusinessNamesSection';
import s from './BusinessNamesSection.scss';
export const BusinessNamesSection = _ref => {
  let query = _ref.query,
    onSelectedNameChange = _ref.onSelectedNameChange,
    onUserSelectedName = _ref.onUserSelectedName,
    _ref$presentation = _ref.presentation,
    presentation = _ref$presentation === void 0 ? 'grid' : _ref$presentation,
    onNamesShown = _ref.onNamesShown;
  const isMobile = useIsMobile();
  const isList = presentation === 'list';
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const _useBusinessNamesSect = useBusinessNamesSection({
      query,
      onSelectedNameChange,
      onUserSelectedName,
      onNamesShown
    }),
    names = _useBusinessNamesSect.names,
    selectedName = _useBusinessNamesSect.selectedName,
    isLoading = _useBusinessNamesSect.isLoading,
    isFetchingMore = _useBusinessNamesSect.isFetchingMore,
    hasError = _useBusinessNamesSect.hasError,
    retry = _useBusinessNamesSect.retry,
    namesPerBatch = _useBusinessNamesSect.namesPerBatch,
    handleSelect = _useBusinessNamesSect.handleSelect,
    handleCtaClick = _useBusinessNamesSect.handleCtaClick,
    handleTooltipView = _useBusinessNamesSect.handleTooltipView,
    ctaLabelKey = _useBusinessNamesSect.ctaLabelKey;
  const getNameKey = (name, idx) => name.id ?? String(idx);
  const openMobileInsights = useCallback(() => {
    if (!selectedName) {
      return;
    }
    if (selectedName.name) {
      handleTooltipView(selectedName.name);
    }
    DomainSearchGateModule.internals.insightsModalsController.businessNameInsights.show({
      name: selectedName
    });
  }, [handleTooltipView, selectedName]);
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? isList ? 'SP4' : '24px' : 'SP5',
    width: "100%",
    dataHook: "business-names-section"
  }, hasError && names.length === 0 ? /*#__PURE__*/React.createElement(BusinessNamesError, {
    onRetry: retry
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: isMobile ? 'top' : 'middle',
    gap: "SP1"
  }, /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0
  }, /*#__PURE__*/React.createElement(AiSparkleIcon, null)), /*#__PURE__*/React.createElement(Heading, {
    size: "small",
    as: "h2",
    className: s.sectionTitle
  }, t('domain_search_gate.bng_results.names_section.title'))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? '20px' : 'SP5',
    width: "100%"
  }, isList ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "12px",
    width: "100%"
  }, isLoading ? /*#__PURE__*/React.createElement(BusinessNameTileSkeletons, {
    count: namesPerBatch,
    keyPrefix: "skeleton"
  }) : names.map((name, idx) => /*#__PURE__*/React.createElement(BusinessNameRow, {
    key: getNameKey(name, idx),
    name: name,
    onSelect: handleSelect
  })), isFetchingMore && /*#__PURE__*/React.createElement(BusinessNameTileSkeletons, {
    count: namesPerBatch,
    keyPrefix: "skeleton-more"
  })) : /*#__PURE__*/React.createElement("div", {
    className: s.grid
  }, /*#__PURE__*/React.createElement(BusinessNameTiles, {
    names: names,
    isLoading: isLoading,
    isFetchingMore: isFetchingMore,
    namesPerBatch: namesPerBatch,
    selectedName: selectedName,
    onSelect: handleSelect,
    onTooltipView: handleTooltipView
  })), isMobile && (isLoading || isFetchingMore) && /*#__PURE__*/React.createElement(BusinessNamesMobileCtaSkeleton, null), hasError ? /*#__PURE__*/React.createElement(BusinessNamesError, {
    onRetry: handleCtaClick
  }) : names.length > 0 && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    direction: isMobile ? 'vertical' : undefined,
    gap: isMobile ? 'SP7' : undefined,
    marginBottom: isMobile ? 'SP6' : undefined
  }, !isList && isMobile && selectedName && /*#__PURE__*/React.createElement(TextButton, {
    onClick: openMobileInsights,
    prefixIcon: /*#__PURE__*/React.createElement(Box, {
      marginRight: "SP0"
    }, /*#__PURE__*/React.createElement(AiSparkleIcon, null)),
    skin: "dark",
    size: "small",
    underline: "always",
    style: {
      color: 'var(--bng-funnel-purple)'
    },
    dataHook: "business-name-insights-cta"
  }, t('domains_search_gate.bng.mobile.results.name_suggestion.ai_insights.cta', {
    nameSuggestion: selectedName.name
  })), isMobile ? /*#__PURE__*/React.createElement(Button, {
    onClick: handleCtaClick,
    disabled: isFetchingMore,
    prefixIcon: isFetchingMore ? /*#__PURE__*/React.createElement(Box, {
      width: 18,
      height: 18,
      align: "center",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(RegenerateSparkle, {
      "aria-hidden": true
    }),
    skin: "dark",
    priority: "secondary",
    size: "medium",
    dataHook: "business-names-generate-button"
  }, t(ctaLabelKey)) : /*#__PURE__*/React.createElement(TextButton, {
    onClick: handleCtaClick,
    disabled: isFetchingMore,
    prefixIcon: isFetchingMore ? /*#__PURE__*/React.createElement(Box, {
      width: 18,
      height: 18,
      align: "center",
      verticalAlign: "middle",
      paddingInlineEnd: "6px"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : /*#__PURE__*/React.createElement(RegenerateSparkle, {
      "aria-hidden": true
    }),
    skin: "dark",
    size: "small",
    underline: "always",
    dataHook: "business-names-cta"
  }, t(ctaLabelKey))))));
};
//# sourceMappingURL=BusinessNamesSection.js.map